// Shared copy for the "what is a layout" messaging shown in both the authenticated
// (LayoutBuilderLanding) and public (PublicLayoutBuilderPage) shells. Centralized so the
// two clients never drift apart on wording.

// Persistent, muted one-liner shown near the top of each client's header, at all times.
export const LAYOUT_BUILDER_SUBTITLE = "Plan the shape of an integration before anything's built."

// Fuller description shown only in the empty/first-run state, before a layout exists.
export const LAYOUT_BUILDER_EMPTY_BLURB = 'A layout is a plan for an integration — the partners, systems, and data flows involved — that you can name, save, and revisit before anything is built.'

// Default/placeholder name given to a brand-new layout in both shells, before the user
// renames it.
export const DEFAULT_LAYOUT_NAME = 'My Integration Layout'

// Flow-count limits (the license's per-flow-type `max_flows`). Layout Builder only ever warns.
export const FLOW_USAGE_HEADING = 'Flows by type · license allowance'
export const FLOW_USAGE_FOOTNOTE = 'Counts are a planning guide — you can add more flows than the license allows to be enabled at a time; extra flows may need an upgraded license.'
export const OVER_LIMIT_TOOLTIP_HEADING = 'Over license limit'

export const overLimitTooltipLine = ({ name, count, max }) =>
  `${name} has ${count} flows; the license allows only ${max} enabled at a time`

export const flowUsageOverSummary = count =>
  `${count} flow type${count === 1 ? '' : 's'} over ${count === 1 ? 'its' : 'their'} limit`

// "used / max", or just "used" when the flow type has no limit.
export const flowUsageValue = (used, max) => max === null ? `${used}` : `${used} / ${max}`

export const flowOverLimitWarning = ({ count, name, max }) =>
  `${count} ${name} flows planned, but the license allows only ${max} enabled ${name} flow${max === 1 ? '' : 's'} at a time.`

// Picker chip label: the curated type name, with its limit in parentheses when it has one.
export const chipWithLimit = (name, limit) => limit === null ? name : `${name} (${limit})`

export const visualBuilderPickerNote = limit =>
  `Sources and destinations from Visual Builder's input and output converters${limit === null ? '' : ` (${limit})`}`

export const FLOW_LIMIT_EXPLANATION = 'Numbers in parentheses show how many flows of that type the license includes.'

export const VISUAL_BUILDER_GUIDE_NOTE = 'This layout is a planning guide. Visual Builder flows can be more complex than what is shown here, allowing multiple file types as well as multiple sources and destinations within a single flow.'

export const NO_NOTES = 'No notes'

// Picker chip detail popover.
export const chipPopoverLabel = label => `${label} details`
export const CHIP_POPOVER_CLOSE = 'Close'
export const licenseIncludes = count => `License includes ${count} flow${count === 1 ? '' : 's'}`

// Applications / file types section of the chip popover.
export const applicationsTabLabel = (side, count) => `${side} (${count})`
export const fileTypeCount = count => `${count} file type${count === 1 ? '' : 's'}`
export const NO_APPLICATIONS = 'None'

// Visual Builder components popover (picker).
export const componentsButtonLabel = count => `Components (${count})`
export const componentFileType = fileType => `File type: ${fileType}`
export const COMPONENTS_TITLE = 'Visual Builder components'
export const COMPONENTS_BLURB = 'Building blocks available in Visual Builder flows.'
export const COMPONENTS_FILTER = 'Filter components…'
export const NO_COMPONENTS_MATCH = 'No components match.'
export const STEP_TYPE_LABELS = {
  launcher: 'Launchers',
  inputConverter: 'Input converters',
  fileProcessor: 'File processors',
  canonicalProcessor: 'Canonical processors',
  outputConverter: 'Output converters',
  sender: 'Senders',
  other: 'Other'
}

// Confirmation text when switching an integration (with flows) to Visual Builder.
export const VISUAL_BUILDER_KEEP_FLOWS = 'Flows will be kept and counted as Visual Builder flows.'

// Implement review: implementing a Visual Builder integration creates no flows.
export const VISUAL_BUILDER_IMPLEMENT_INTEGRATION_ONLY = 'Implementing creates the integration only. Build each flow in Visual Builder afterwards, starting from its AI prompt.'

// Picker section offered when no specific integration covers every picked data type.
export const VISUAL_BUILDER_ALTERNATIVE_HEADING = 'Or, build it with Visual Builder'

// Implement review dialog instructions.
export const IMPLEMENT_REVIEW_INTRO = 'Choose the integrations you want to implement. Each one is created in this workspace as a new integration, with a draft flow for every flow you keep. Nothing is deployed: you finish setting up and deploy each flow afterwards.'
export const implementAddIntro = integrationName =>
  `Choose the flows to add to ${integrationName}. Each one is created as a draft flow; nothing is deployed.`
// Add mode always adds the one flow the user picked, so name it instead of asking to choose flows.
export const implementAddFlowIntro = (flowName, integrationName) =>
  `Add “${flowName}” to ${integrationName}. It's created as a draft flow; nothing is deployed.`
export const flowNameExistingWarning = (name, integrationName) => `A flow named “${name}” already exists in ${integrationName}.`
export const flowNameDuplicateWarning = name => `“${name}” is also used by another flow here.`
export const IMPLEMENT_FLOW_NAMES_UNCHECKED_NOTE = 'Could not check the integration for existing flow names, so flow names are only compared with each other.'
export const IMPLEMENT_INCLUDE_LABEL = 'Implement this integration'
export const IMPLEMENT_NAME_HELP = 'This is the name shown in your Integrations list. You can change it later.'
export const integrationNameWorkspaceWarning = name => `An integration named “${name}” already exists in this workspace.`
export const integrationNameBatchWarning = name => `Another integration in this batch is named “${name}”.`
export const IMPLEMENT_NAMES_UNCHECKED_NOTE = 'Could not check the workspace for existing integration names, so names are not de-duplicated.'
export const IMPLEMENT_FLOWS_HEADING = 'Flows'
export const implementFlowsHelp = integrationName =>
  `Choose a flow type for each flow. File types are optional: leave them blank to choose them later in the flow editor. Uncheck Include on a flow to skip it; you can add it to ${integrationName} later with “Implement in integration” on the flow.`
export const IMPLEMENT_USAGE_NOTE = 'How many flows of each type you\'re implementing, against your license allowance of enabled flows. Going over is allowed here, but deploying or enabling a flow beyond the allowance will fail.'
export const implementButtonLabel = count => `Implement ${count} integration${count === 1 ? '' : 's'}`
export const addFlowsButtonLabel = count => `Add ${count} flow${count === 1 ? '' : 's'}`

// Remove from layout: confirmation for removing an integration or a flow from the layout. `implementedNames`
// are the integrations created from it, which removing it from the plan never touches.
export const REMOVE_FROM_LAYOUT_ACCEPT = 'Remove from layout'
export const removeFromLayoutQuestion = name => `Remove ${name} from this layout?`
export const REMOVE_IMPLEMENTED_INTRO = 'It was implemented as:'
export const REMOVE_IMPLEMENTED_NOTE = "Removing it from the layout doesn't delete those integrations or their flows; they just won't show as implemented here any more."

// Link-style import affordance on the public planner's first-run landing: the prompt text, then the
// clickable control (named by IMPORT_LAYOUT_LINK) that opens the file chooser.
export const IMPORT_LAYOUT_PROMPT = 'Already have a layout file?'
export const IMPORT_LAYOUT_LINK = 'Import here'
